import { useMemo, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';

import { SearchInput } from 'src/components/SearchInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { maskString, validateBIC, validateIBAN } from 'src/utils/validations';
export interface IEditPaymentDetailsModalContentProps {
    handleClose: () => void;
    handleAction: () => void;
    message: string;
    buttonText?: string;
    closeModalButtonText?: string;
    initialValues?: object;
    handleFormSubmit?: (values: object) => void;
}

const EditPaymentDetailsModalContent = ({
    handleClose,
    message,
    buttonText,
    closeModalButtonText = 'Cancel',
    initialValues,
    handleFormSubmit,
}: IEditPaymentDetailsModalContentProps) => {
    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                handleFormSubmit(values);
                handleClose();
            }}
            validate={(values) => {
                const errors: {
                    iban?: string;
                    bic?: string;
                } = {};
                if (!values.iban) {
                    errors.iban = 'IBAN is required';
                }
                if (!values.bic) {
                    errors.bic = 'BIC is required';
                }
                return errors;
            }}
        >
            {({ errors, handleChange, handleSubmit, values, setFieldError }) => {return (
                <EditPaymentDetailsForm
                    errors={errors}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    values={values}
                    setFieldError={setFieldError}
                    handleClose={handleClose}
                    message={message ?? 'Enter new bank details'}
                    buttonText={buttonText}
                    closeModalButtonText={closeModalButtonText}
                />
            );}}
        </Formik>
    );
};

type EditPaymentDetailsFormProps = {
    errors: object;
    handleChange: {
        (e: React.ChangeEvent<any>): void;
        <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
            ? void
            : (e: string | React.ChangeEvent<any>) => void;
    };
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    values: object;
    setFieldError: (field: string, message: string) => void;
    handleClose: () => void;
    handleAction: () => void;
    message: string;
    buttonText?: string;
    closeModalButtonText?: string;
};

const EditPaymentDetailsForm: React.FC<EditPaymentDetailsFormProps> = ({
    errors,
    handleChange,
    handleSubmit,
    values,
    setFieldError,
    handleClose,
    message,
    buttonText,
    closeModalButtonText,
}) => {
    const isBICValid = useMemo(() => {
        return validateBIC(values?.bic);
    }, [values?.bic]);
    const isIBANValid = useMemo(() => {
        return validateIBAN(values?.iban);
    }, [values?.iban]);

    const [maskedBIC, setMaskedBIC] = useState<string>('');
    const [maskedIBAN, setMaskedIBAN] = useState<string>('');

    const handleValidateIBAN = (iban: string, shouldShowSuccessToast = true) => {
        if (isIBANValid) {
            if (shouldShowSuccessToast) {
                showSuccessToast('IBAN is valid');
            }
            setMaskedIBAN(maskString(iban));
        } else {
            showErrorToast('IBAN is not valid');
            setFieldError('iban', 'IBAN is not valid');
            setMaskedIBAN('');
        }
    };

    const handleValidateBIC = (bic: string, shouldShowSuccessToast = true) => {
        if (isBICValid) {
            if (shouldShowSuccessToast) {
                showSuccessToast('BIC is valid');
            }
            setMaskedBIC(maskString(bic));
        } else {
            setFieldError('bic', 'BIC is not valid');
            showErrorToast('BIC is not valid');
            setMaskedBIC('');
        }
    };

    const handleClearBICField = () => {
        setMaskedBIC('');
    };

    const handleClearIBANField = () => {
        setMaskedIBAN('');
    };

    return (
        <Form
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(values);
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#000',
                    }}
                >
                    Edit Payment Details
                </Typography>

                {message && <Typography>{message}</Typography>}

                <SearchInput
                    name="bic"
                    label="BIC"
                    value={values.bic}
                    onChange={handleChange}
                    onClearField={handleClearBICField}
                    error={!!errors.bic || !isBICValid ? errors.bic ?? 'BIC is Invalid' : undefined}
                    searchText="Validate"
                    onSearch={handleValidateBIC}
                    maskedValue={maskedBIC}
                    boxStyling={{
                        width: '100%',
                    }}
                />

                <SearchInput
                    name="iban"
                    label="IBAN"
                    value={values.iban}
                    onChange={handleChange}
                    onClearField={handleClearIBANField}
                    error={
                        !!errors.iban || !isIBANValid ? errors.iban ?? 'IBAN is Invalid' : undefined
                    }
                    searchText="Validate"
                    onSearch={handleValidateIBAN}
                    maskedValue={maskedIBAN}
                />

                <Stack direction="row" justifyContent="space-between" gap={2} sx={{ mt: 3 }}>
                    <Button
                        variant="outlined-black"
                        onClick={handleClose}
                        sx={{ width: '100%', height: '44px' }}
                    >
                        {closeModalButtonText}
                    </Button>

                    <Button
                        type="submit"
                        variant="main"
                        sx={{ width: '100%', height: '44px' }}
                        disabled={!isBICValid || !isIBANValid}
                    >
                        {buttonText}
                    </Button>
                </Stack>
            </Box>
        </Form>
    );
};

export default EditPaymentDetailsModalContent;
